import { CorporationDataType } from '@commenda/types/src/corporation';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { getCorporationName, getPrintableDirectorName } from '@commenda/utils';
import { LegacyDocumentType, StakeholderType } from '@prisma/client';
import { ZodError } from 'zod';
import { checkEmailValidityForDirector } from '../../../common/helpers';
import { corporationDetailsSchema } from '../../common/corporateStakeholder/CorporationDetailsForm';
import { TShareholderVsSharePercentageMap } from '../../EquityInfo/types';
import { getIdentityProof } from '../utils';
import { directorSchema } from './schema';

const validateDirectorInfo = (
  directors: DirectorBase[],
  selectedDirectorIds: number[],
  corporationId: number
) => {
  const directorWithError = [];
  const selectedDirectors = directors?.filter((director) =>
    selectedDirectorIds.includes(director.id)
  );

  for (let i = 0; i < selectedDirectorIds.length; i += 1) {
    const identityProofFile = getIdentityProof(
      selectedDirectors[i],
      corporationId
    );
    const passportSizePhotoFile = selectedDirectors[i]?.directorFiles
      ?.sort((a, b) => b.id - a.id)
      .find(
        (eachFile) =>
          eachFile.documentType === LegacyDocumentType.PassportSizePhoto
      );

    const specimenSignatureFile = selectedDirectors[i]?.directorFiles
      ?.sort((a, b) => b.id - a.id)
      .find(
        (eachFile) =>
          eachFile.documentType === LegacyDocumentType.SpecimenSignature
      );
    const validationResult = directorSchema.safeParse({
      ...selectedDirectors[i],
      identityProof: identityProofFile,
      passportSizePhoto: passportSizePhotoFile,
      specimenSignature: specimenSignatureFile,
    }) as {
      success: boolean;
      error: any;
    };

    if (!validationResult.success) {
      if (validationResult.error) {
        directorWithError.push({
          name: getPrintableDirectorName(selectedDirectors[i]),
          id: selectedDirectors[i]?.id,
        });
      }
    }
  }

  return directorWithError;
};

const validateIndianLlpStakeholders = (
  selectedStakeholders: TShareholderVsSharePercentageMap,
  {
    directors,
    corporations,
    corporationId,
  }: {
    directors: DirectorBase[];
    corporations: CorporationDataType[];
    corporationId: number;
  }
) => {
  // Find the company email from the corporations array
  const currentCorporation = corporations.find(
    (corp) => corp.id === corporationId
  );
  const companyEmail = currentCorporation?.contactEmail;

  // Filter directors to only include those belonging to THIS LLP
  const llpDirectors = directors.filter((dir) =>
    dir.corporationHasDirector?.find(
      (dirWithCorp) =>
        dirWithCorp.corporationId === corporationId &&
        dirWithCorp.additionalDirectorInformation?.isDirector === true
    )
  );

  // Use ONLY LLP-specific directors for email uniqueness validation
  const companyEmailLower = companyEmail?.trim().toLowerCase();
  const llpDirectorEmails =
    llpDirectors?.map((d) => d.email?.trim().toLowerCase()).filter(Boolean) ||
    [];

  const shareholderWithError = [];
  const emailConflicts = []; // Track email conflicts separately

  Object.keys(selectedStakeholders).forEach((stakeholderId) => {
    const [type, id] = stakeholderId.split('_');

    if (type === StakeholderType.INDIVIDUAL) {
      const director = directors.find((d) => d.id === +id);

      if (!director) return;

      const isValid = checkEmailValidityForDirector(
        director,
        getPrintableDirectorName(director),
        stakeholderId,
        companyEmailLower,
        llpDirectorEmails,
        llpDirectors,
        emailConflicts,
        shareholderWithError,
        getPrintableDirectorName
      );

      if (!isValid) return;

      // Run other director validations
      const directorWithError = validateDirectorInfo(
        directors,
        [director.id],
        corporationId
      );

      if (directorWithError.length > 0) {
        shareholderWithError.push({
          name: getPrintableDirectorName(director),
          id: stakeholderId,
        });
      }
    }

    if (type === StakeholderType.INSTITUTION) {
      const corporation = corporations.find((c) => c.id === +id);

      if (!corporation) return;

      // Basic corporation validation
      const corporationBasicInformationValidationResult =
        corporationDetailsSchema.safeParse(corporation) as {
          success: boolean;
          error: ZodError;
        };

      if (!corporationBasicInformationValidationResult.success) {
        shareholderWithError.push({
          name: getCorporationName(corporation),
          id: stakeholderId,
        });
        return;
      }

      const authorizedSignatory = corporation.corporationHasDirector?.find(
        (director) =>
          director.additionalDirectorInformation?.isAuthorizedSignatory
      );

      if (authorizedSignatory) {
        // Find the director data for the authorized signatory
        const signatoryDirector = directors.find(
          (d) => d.id === authorizedSignatory.directorId
        );

        // Check email uniqueness for authorized signatory (against LLP directors only)
        if (signatoryDirector) {
          const isValid = checkEmailValidityForDirector(
            signatoryDirector,
            getCorporationName(corporation),
            stakeholderId,
            companyEmailLower,
            llpDirectorEmails,
            llpDirectors,
            emailConflicts,
            shareholderWithError,
            getPrintableDirectorName
          );

          if (!isValid) return;
        }

        // Run other director validations for the signatory
        const directorValidationResult = validateDirectorInfo(
          directors,
          [authorizedSignatory.directorId],
          corporationId
        );

        if (directorValidationResult.length > 0) {
          shareholderWithError.push({
            name: getCorporationName(corporation),
            id: stakeholderId,
          });
        }
      } else {
        shareholderWithError.push({
          name: getCorporationName(corporation),
          id: stakeholderId,
        });
      }
    }
  });

  // Generate detailed error message if there are email conflicts
  if (emailConflicts.length > 0) {
    const emailErrorMessage = emailConflicts
      .map((conflict) => {
        if (conflict.conflictType === 'company') {
          return `${conflict.stakeholderName} (${conflict.stakeholderEmail}) has the same email as the company.`;
        } else {
          return `${conflict.stakeholderName} (${conflict.stakeholderEmail}) has the same email as ${conflict.conflictTarget}.`;
        }
      })
      .join(' ');

    // Store the detailed error message on the result
    (shareholderWithError as any).emailErrorMessage =
      `Email conflicts found: ${emailErrorMessage}`;
  }

  return shareholderWithError;
};

export { validateDirectorInfo, validateIndianLlpStakeholders };
